'use client';

import { c as _c } from "react/compiler-runtime";
import { type SyntheticEvent, useCallback } from 'react';
import { SPARTAN_PRODUCT_HERO_BANNER_SCENE_SCROLL_SPEED, SPARTAN_PRODUCT_HERO_MOBILE_DPR_2_LAYER_IMAGE_WIDTHS } from '@/app/lcv/components/sections/spartan/products/hero-banner/constants';
import { SpartanProductHeroBannerParallaxCar } from '@/app/lcv/components/sections/spartan/products/hero-banner/media/parallax-scene/car';
import { SpartanProductHeroBannerParallaxLayer } from '@/app/lcv/components/sections/spartan/products/hero-banner/media/parallax-scene/layer';
import styles from '@/app/lcv/components/sections/spartan/products/hero-banner/media/parallax-scene/layout.module.css';
import { SpartanProductHeroBannerSpecialLogoScene } from '@/app/lcv/components/sections/spartan/products/hero-banner/media/parallax-scene/special-logo';
import type { SpartanProductHeroBannerParallaxSceneProps } from '@/app/lcv/components/sections/spartan/products/hero-banner/media/parallax-scene/types';
import { useEntranceReveal } from '@/app/lcv/components/sections/spartan/products/hero-banner/media/parallax-scene/useEntranceReveal';
import { SpartanProductHeroBannerOverlays } from '@/app/lcv/components/sections/spartan/products/hero-banner/overlays';
import { SPARTAN_PRODUCT_HERO_BANNER_TRACKING } from '@/app/lcv/components/sections/spartan/products/hero-banner/tracking';
import { getSpartanProductHeroBannerEntranceAnimation } from '@/app/lcv/components/sections/spartan/products/hero-banner/utils';
import { getSpartanProductHeroParallaxStyle } from '@/app/lcv/components/sections/spartan/products/hero-banner/utils/parallax-style';
import { getSpartanProductHeroBannerSceneLayoutStyle } from '@/app/lcv/components/sections/spartan/products/hero-banner/utils/scene-layout';
import { getSpartanParallaxProps } from '@/app/lcv/components/sections/spartan/products/shared/product-parallax-scroll';
import { useSpartanScrollRefresh } from '@/app/lcv/components/sections/spartan/products/shared/product-scroll/useSpartanScrollRefresh';
import { cn } from '@/app/utils/cn';
import { createDataAttributes } from '@/app/utils/createDataAttributes';
import { safeCall } from '@/app/utils/safeCall';
export const SpartanProductHeroBannerParallaxScene = t0 => {
  const $ = _c(134);
  const {
    parallax,
    background,
    lcpMedia,
    isEntranceReady,
    isEntranceSettled: t1,
    carImageRef,
    criticalImageRef,
    onCarLoad,
    onCarError,
    onCriticalLoad,
    onCriticalError,
    onEntranceSettled,
    onEntranceReady,
    title: t2,
    specialLogo,
    overlay
  } = t0;
  const isEntranceSettled = t1 === undefined ? false : t1;
  const title = t2 === undefined ? "" : t2;
  const refreshScroll = useSpartanScrollRefresh();
  let t3;
  if ($[0] !== parallax) {
    t3 = getSpartanProductHeroBannerEntranceAnimation(parallax);
    $[0] = parallax;
    $[1] = t3;
  } else {
    t3 = $[1];
  }
  const entranceAnimation = t3;
  const hasEntranceReveal = !!entranceAnimation;
  const hasActiveDepthGlideEntrance = entranceAnimation === "active-depth-glide";
  const hasCityRoadSurgeEntrance = entranceAnimation === "city-road-surge";
  const hasLuxuryGlideInEntrance = entranceAnimation === "luxury-glide-in";
  const hasMountainSurgeEntrance = entranceAnimation === "mountain-surge";
  const hasShowroomDriveInEntrance = entranceAnimation === "showroom-drive-in";
  const hasShowroomDepthGlideEntrance = entranceAnimation === "showroom-depth-glide";
  const hasWindingRoadDriveInEntrance = entranceAnimation === "winding-road-drive-in";
  const hasPanZoomEntrance = hasActiveDepthGlideEntrance || hasCityRoadSurgeEntrance || hasLuxuryGlideInEntrance || hasMountainSurgeEntrance || hasShowroomDepthGlideEntrance || hasShowroomDriveInEntrance || hasWindingRoadDriveInEntrance;
  const hasCriticalSceneMedia = lcpMedia === "car" || lcpMedia === "back" && !!parallax.back || lcpMedia === "mid" && !!parallax.mid || lcpMedia === "front" && !!parallax.front;
  const t4 = lcpMedia === "car";
  let t5;
  if ($[2] !== hasCriticalSceneMedia || $[3] !== hasEntranceReveal || $[4] !== onEntranceReady || $[5] !== t4) {
    t5 = {
      hasCriticalSceneMedia,
      hasEntranceReveal,
      isCarCritical: t4,
      onSceneReady: onEntranceReady
    };
    $[2] = hasCriticalSceneMedia;
    $[3] = hasEntranceReveal;
    $[4] = onEntranceReady;
    $[5] = t4;
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  const legacyEntrance = useEntranceReveal(t5);
  const hasExternalEntranceCoordinator = isEntranceReady !== undefined;
  const resolvedEntranceReady = hasExternalEntranceCoordinator ? isEntranceReady : legacyEntrance.isSceneReady;
  const resolvedCarReady = hasExternalEntranceCoordinator ? isEntranceReady : legacyEntrance.isCarReady;
  const resolvedCarImageRef = hasExternalEntranceCoordinator ? carImageRef : legacyEntrance.carImageRef;
  const resolvedCriticalImageRef = hasExternalEntranceCoordinator ? criticalImageRef : legacyEntrance.criticalImageRef;
  const resolvedCarLoad = hasExternalEntranceCoordinator ? onCarLoad : legacyEntrance.handleCarMediaLoad;
  const resolvedCarError = hasExternalEntranceCoordinator ? onCarError : legacyEntrance.handleCarMediaError;
  const resolvedCriticalLoad = hasExternalEntranceCoordinator ? onCriticalLoad : legacyEntrance.handleCriticalMediaLoad;
  const resolvedCriticalError = hasExternalEntranceCoordinator ? onCriticalError : legacyEntrance.handleCriticalMediaError;
  let t6;
  if ($[7] !== refreshScroll || $[8] !== resolvedCarLoad) {
    t6 = event => {
      refreshScroll();
      safeCall(resolvedCarLoad, event);
    };
    $[7] = refreshScroll;
    $[8] = resolvedCarLoad;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  const handleCarLoad = t6;
  let t7;
  if ($[10] !== parallax.scene) {
    t7 = getSpartanProductHeroBannerSceneLayoutStyle(parallax.scene);
    $[10] = parallax.scene;
    $[11] = t7;
  } else {
    t7 = $[11];
  }
  const sceneLayoutStyle = t7;
  const sceneScrollSpeed = parallax.scrollSpeed ?? SPARTAN_PRODUCT_HERO_BANNER_SCENE_SCROLL_SPEED;
  let t8;
  if ($[12] !== sceneScrollSpeed) {
    t8 = getSpartanProductHeroParallaxStyle(sceneScrollSpeed);
    $[12] = sceneScrollSpeed;
    $[13] = t8;
  } else {
    t8 = $[13];
  }
  let t9;
  if ($[14] !== sceneScrollSpeed) {
    t9 = getSpartanParallaxProps(sceneScrollSpeed);
    $[14] = sceneScrollSpeed;
    $[15] = t9;
  } else {
    t9 = $[15];
  }
  let t10;
  if ($[16] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = createDataAttributes({
      test: SPARTAN_PRODUCT_HERO_BANNER_TRACKING.parallaxScrollScene
    });
    $[16] = t10;
  } else {
    t10 = $[16];
  }
  const t11 = hasCityRoadSurgeEntrance || hasWindingRoadDriveInEntrance;
  const t12 = hasShowroomDriveInEntrance && parallax.carEntranceDirection !== "right-to-left";
  const t13 = hasPanZoomEntrance && resolvedEntranceReady;
  const t14 = parallax.fadeBackgroundOnEntrance === false;
  let t15;
  if ($[17] !== hasActiveDepthGlideEntrance || $[18] !== hasCityRoadSurgeEntrance || $[19] !== hasLuxuryGlideInEntrance || $[20] !== hasMountainSurgeEntrance || $[21] !== hasPanZoomEntrance || $[22] !== hasShowroomDepthGlideEntrance || $[23] !== hasShowroomDriveInEntrance || $[24] !== hasWindingRoadDriveInEntrance || $[25] !== isEntranceSettled || $[26] !== t11 || $[27] !== t12 || $[28] !== t13 || $[29] !== t14) {
    t15 = cn(styles.cameraStage, {
      [styles.cameraPanZoom]: hasPanZoomEntrance,
      [styles.cameraRoadPanZoom]: t11,
      [styles.cameraCityRoadSurge]: hasCityRoadSurgeEntrance,
      [styles.cameraActiveDepthGlide]: hasActiveDepthGlideEntrance,
      [styles.cameraLuxuryGlideIn]: hasLuxuryGlideInEntrance,
      [styles.cameraMountainSurge]: hasMountainSurgeEntrance,
      [styles.cameraWindingRoadDriveIn]: hasWindingRoadDriveInEntrance,
      [styles.cameraShowroomDriveIn]: hasShowroomDriveInEntrance,
      [styles.cameraShowroomDepthGlide]: hasShowroomDepthGlideEntrance,
      [styles.cameraShowroomDriveInLeftToRight]: t12,
      [styles.cameraPanZoomReady]: t13,
      [styles.cameraEntranceFadeDisabled]: t14,
      [styles.entranceMotionSettled]: isEntranceSettled
    });
    $[17] = hasActiveDepthGlideEntrance;
    $[18] = hasCityRoadSurgeEntrance;
    $[19] = hasLuxuryGlideInEntrance;
    $[20] = hasMountainSurgeEntrance;
    $[21] = hasPanZoomEntrance;
    $[22] = hasShowroomDepthGlideEntrance;
    $[23] = hasShowroomDriveInEntrance;
    $[24] = hasWindingRoadDriveInEntrance;
    $[25] = isEntranceSettled;
    $[26] = t11;
    $[27] = t12;
    $[28] = t13;
    $[29] = t14;
    $[30] = t15;
  } else {
    t15 = $[30];
  }
  let t16;
  if ($[31] !== overlay) {
    t16 = <SpartanProductHeroBannerOverlays overlay={overlay} placement="root" stack="under-environment" />;
    $[31] = overlay;
    $[32] = t16;
  } else {
    t16 = $[32];
  }
  let t17;
  if ($[33] === Symbol.for("react.memo_cache_sentinel")) {
    t17 = cn(styles.frame, styles.environmentFrame);
    $[33] = t17;
  } else {
    t17 = $[33];
  }
  let t18;
  if ($[34] === Symbol.for("react.memo_cache_sentinel")) {
    t18 = createDataAttributes({
      test: SPARTAN_PRODUCT_HERO_BANNER_TRACKING.parallaxFrame
    });
    $[34] = t18;
  } else {
    t18 = $[34];
  }
  const t19 = hasEntranceReveal && resolvedEntranceReady;
  const t20 = hasCityRoadSurgeEntrance || hasWindingRoadDriveInEntrance;
  const t21 = hasShowroomDriveInEntrance && parallax.carEntranceDirection !== "right-to-left";
  let t22;
  if ($[35] !== hasActiveDepthGlideEntrance || $[36] !== hasCityRoadSurgeEntrance || $[37] !== hasLuxuryGlideInEntrance || $[38] !== hasMountainSurgeEntrance || $[39] !== hasPanZoomEntrance || $[40] !== hasShowroomDepthGlideEntrance || $[41] !== hasShowroomDriveInEntrance || $[42] !== hasWindingRoadDriveInEntrance || $[43] !== t19 || $[44] !== t20 || $[45] !== t21) {
    t22 = cn(styles.scene, {
      [styles.revealReady]: t19,
      [styles.environmentPanZoom]: hasPanZoomEntrance,
      [styles.environmentRoadPanZoom]: t20,
      [styles.environmentCityRoadSurge]: hasCityRoadSurgeEntrance,
      [styles.environmentActiveDepthGlide]: hasActiveDepthGlideEntrance,
      [styles.environmentLuxuryGlideIn]: hasLuxuryGlideInEntrance,
      [styles.environmentMountainSurge]: hasMountainSurgeEntrance,
      [styles.environmentWindingRoadDriveIn]: hasWindingRoadDriveInEntrance,
      [styles.environmentShowroomDriveIn]: hasShowroomDriveInEntrance,
      [styles.environmentShowroomDepthGlide]: hasShowroomDepthGlideEntrance,
      [styles.environmentShowroomDriveInLeftToRight]: t21
    });
    $[35] = hasActiveDepthGlideEntrance;
    $[36] = hasCityRoadSurgeEntrance;
    $[37] = hasLuxuryGlideInEntrance;
    $[38] = hasMountainSurgeEntrance;
    $[39] = hasPanZoomEntrance;
    $[40] = hasShowroomDepthGlideEntrance;
    $[41] = hasShowroomDriveInEntrance;
    $[42] = hasWindingRoadDriveInEntrance;
    $[43] = t19;
    $[44] = t20;
    $[45] = t21;
    $[46] = t22;
  } else {
    t22 = $[46];
  }
  let t23;
  if ($[47] === Symbol.for("react.memo_cache_sentinel")) {
    t23 = createDataAttributes({
      test: SPARTAN_PRODUCT_HERO_BANNER_TRACKING.parallaxScene
    });
    $[47] = t23;
  } else {
    t23 = $[47];
  }
  let t24;
  if ($[48] !== hasEntranceReveal || $[49] !== lcpMedia || $[50] !== parallax.back || $[51] !== parallax.scene || $[52] !== resolvedCriticalError || $[53] !== resolvedCriticalImageRef || $[54] !== resolvedCriticalLoad) {
    t24 = !!parallax.back && <SpartanProductHeroBannerParallaxLayer media={parallax.back} scene={parallax.scene} layoutPrefix="layer-back" className={styles.layerBack} testId={SPARTAN_PRODUCT_HERO_BANNER_TRACKING.parallaxLayer.back} mediaTestId={SPARTAN_PRODUCT_HERO_BANNER_TRACKING.parallaxBg.back} mobileWidths={SPARTAN_PRODUCT_HERO_MOBILE_DPR_2_LAYER_IMAGE_WIDTHS} isLcp={lcpMedia === "back"} imageRef={hasEntranceReveal && lcpMedia === "back" ? resolvedCriticalImageRef : undefined} onLoad={hasEntranceReveal && lcpMedia === "back" ? resolvedCriticalLoad : undefined} onError={hasEntranceReveal && lcpMedia === "back" ? resolvedCriticalError : undefined} />;
    $[48] = hasEntranceReveal;
    $[49] = lcpMedia;
    $[50] = parallax.back;
    $[51] = parallax.scene;
    $[52] = resolvedCriticalError;
    $[53] = resolvedCriticalImageRef;
    $[54] = resolvedCriticalLoad;
    $[55] = t24;
  } else {
    t24 = $[55];
  }
  let t25;
  if ($[56] !== hasEntranceReveal || $[57] !== lcpMedia || $[58] !== parallax.mid || $[59] !== parallax.scene || $[60] !== resolvedCriticalError || $[61] !== resolvedCriticalImageRef || $[62] !== resolvedCriticalLoad) {
    t25 = !!parallax.mid && <SpartanProductHeroBannerParallaxLayer media={parallax.mid} scene={parallax.scene} layoutPrefix="layer-mid" className={styles.layerMid} testId={SPARTAN_PRODUCT_HERO_BANNER_TRACKING.parallaxLayer.mid} mediaTestId={SPARTAN_PRODUCT_HERO_BANNER_TRACKING.parallaxBg.mid} mobileWidths={SPARTAN_PRODUCT_HERO_MOBILE_DPR_2_LAYER_IMAGE_WIDTHS} isLcp={lcpMedia === "mid"} imageRef={hasEntranceReveal && lcpMedia === "mid" ? resolvedCriticalImageRef : undefined} onLoad={hasEntranceReveal && lcpMedia === "mid" ? resolvedCriticalLoad : undefined} onError={hasEntranceReveal && lcpMedia === "mid" ? resolvedCriticalError : undefined} />;
    $[56] = hasEntranceReveal;
    $[57] = lcpMedia;
    $[58] = parallax.mid;
    $[59] = parallax.scene;
    $[60] = resolvedCriticalError;
    $[61] = resolvedCriticalImageRef;
    $[62] = resolvedCriticalLoad;
    $[63] = t25;
  } else {
    t25 = $[63];
  }
  let t26;
  if ($[64] !== hasEntranceReveal || $[65] !== lcpMedia || $[66] !== parallax.front || $[67] !== parallax.scene || $[68] !== resolvedCriticalError || $[69] !== resolvedCriticalImageRef || $[70] !== resolvedCriticalLoad) {
    t26 = !!parallax.front && <SpartanProductHeroBannerParallaxLayer media={parallax.front} scene={parallax.scene} layoutPrefix="layer-front" className={styles.layerFront} testId={SPARTAN_PRODUCT_HERO_BANNER_TRACKING.parallaxLayer.front} mediaTestId={SPARTAN_PRODUCT_HERO_BANNER_TRACKING.parallaxBg.front} isLcp={lcpMedia === "front"} imageRef={hasEntranceReveal && lcpMedia === "front" ? resolvedCriticalImageRef : undefined} onLoad={hasEntranceReveal && lcpMedia === "front" ? resolvedCriticalLoad : undefined} onError={hasEntranceReveal && lcpMedia === "front" ? resolvedCriticalError : undefined} />;
    $[64] = hasEntranceReveal;
    $[65] = lcpMedia;
    $[66] = parallax.front;
    $[67] = parallax.scene;
    $[68] = resolvedCriticalError;
    $[69] = resolvedCriticalImageRef;
    $[70] = resolvedCriticalLoad;
    $[71] = t26;
  } else {
    t26 = $[71];
  }
  let t27;
  if ($[72] !== t22 || $[73] !== t24 || $[74] !== t25 || $[75] !== t26) {
    t27 = <div className={t22} {...t23}>{t24}{t25}{t26}</div>;
    $[72] = t22;
    $[73] = t24;
    $[74] = t25;
    $[75] = t26;
    $[76] = t27;
  } else {
    t27 = $[76];
  }
  let t28;
  if ($[77] !== sceneLayoutStyle || $[78] !== t27) {
    t28 = <div className={t17} style={sceneLayoutStyle} {...t18}>{t27}</div>;
    $[77] = sceneLayoutStyle;
    $[78] = t27;
    $[79] = t28;
  } else {
    t28 = $[79];
  }
  let t29;
  if ($[80] !== background || $[81] !== t15 || $[82] !== t16 || $[83] !== t28) {
    t29 = <div className={t15}>{background}{t16}{t28}</div>;
    $[80] = background;
    $[81] = t15;
    $[82] = t16;
    $[83] = t28;
    $[84] = t29;
  } else {
    t29 = $[84];
  }
  let t30;
  let t31;
  if ($[85] !== overlay) {
    t30 = <SpartanProductHeroBannerOverlays overlay={overlay} placement="root" stack="over-environment" />;
    t31 = <SpartanProductHeroBannerOverlays overlay={overlay} placement="root" stack="over-foreground" />;
    $[85] = overlay;
    $[86] = t30;
    $[87] = t31;
  } else {
    t30 = $[86];
    t31 = $[87];
  }
  let t32;
  if ($[88] === Symbol.for("react.memo_cache_sentinel")) {
    t32 = cn(styles.frame, styles.foregroundFrame);
    $[88] = t32;
  } else {
    t32 = $[88];
  }
  const t33 = hasEntranceReveal && resolvedEntranceReady;
  const t34 = parallax.carEntranceDirection === "right-to-left";
  let t35;
  if ($[89] !== hasActiveDepthGlideEntrance || $[90] !== hasCityRoadSurgeEntrance || $[91] !== hasLuxuryGlideInEntrance || $[92] !== hasMountainSurgeEntrance || $[93] !== hasPanZoomEntrance || $[94] !== hasShowroomDepthGlideEntrance || $[95] !== hasShowroomDriveInEntrance || $[96] !== hasWindingRoadDriveInEntrance || $[97] !== t33 || $[98] !== t34) {
    t35 = cn(styles.scene, {
      [styles.revealReady]: t33,
      [styles.foregroundPanZoom]: hasPanZoomEntrance,
      [styles.foregroundCityRoadSurge]: hasCityRoadSurgeEntrance,
      [styles.foregroundLuxuryGlideIn]: hasLuxuryGlideInEntrance,
      [styles.foregroundActiveDepthGlide]: hasActiveDepthGlideEntrance,
      [styles.foregroundMountainSurge]: hasMountainSurgeEntrance,
      [styles.foregroundShowroomDriveIn]: hasShowroomDriveInEntrance,
      [styles.foregroundShowroomDepthGlide]: hasShowroomDepthGlideEntrance,
      [styles.foregroundWindingRoadDriveIn]: hasWindingRoadDriveInEntrance,
      [styles.carEntranceRightToLeft]: t34
    });
    $[89] = hasActiveDepthGlideEntrance;
    $[90] = hasCityRoadSurgeEntrance;
    $[91] = hasLuxuryGlideInEntrance;
    $[92] = hasMountainSurgeEntrance;
    $[93] = hasPanZoomEntrance;
    $[94] = hasShowroomDepthGlideEntrance;
    $[95] = hasShowroomDriveInEntrance;
    $[96] = hasWindingRoadDriveInEntrance;
    $[97] = t33;
    $[98] = t34;
    $[99] = t35;
  } else {
    t35 = $[99];
  }
  let t36;
  if ($[100] !== parallax.blur || $[101] !== parallax.scene) {
    t36 = !!parallax.blur && <SpartanProductHeroBannerParallaxLayer media={parallax.blur} scene={parallax.scene} layoutPrefix="layer-blur" className={styles.layerBlur} testId={SPARTAN_PRODUCT_HERO_BANNER_TRACKING.parallaxLayer.blur} mediaTestId={SPARTAN_PRODUCT_HERO_BANNER_TRACKING.parallaxBg.blur} loading="eager" mobileWidths={[640, 750]} desktopWidths={[640, 750]} />;
    $[100] = parallax.blur;
    $[101] = parallax.scene;
    $[102] = t36;
  } else {
    t36 = $[102];
  }
  const t37 = parallax.front?.scrollSpeed;
  const t38 = hasEntranceReveal ? resolvedCarImageRef : undefined;
  const t39 = hasEntranceReveal ? resolvedCarError : undefined;
  const t40 = lcpMedia === "car";
  let t41;
  if ($[103] !== handleCarLoad || $[104] !== hasEntranceReveal || $[105] !== isEntranceSettled || $[106] !== onEntranceSettled || $[107] !== parallax.car || $[108] !== parallax.scene || $[109] !== resolvedCarReady || $[110] !== t37 || $[111] !== t38 || $[112] !== t39 || $[113] !== t40) {
    t41 = <SpartanProductHeroBannerParallaxCar media={parallax.car} scene={parallax.scene} scrollSpeed={t37} imageRef={t38} onLoad={handleCarLoad} onError={t39} isEntranceRevealEnabled={hasEntranceReveal} isEntranceReady={resolvedCarReady} isEntranceSettled={isEntranceSettled} onEntranceSettled={onEntranceSettled} isLcp={t40} />;
    $[103] = handleCarLoad;
    $[104] = hasEntranceReveal;
    $[105] = isEntranceSettled;
    $[106] = onEntranceSettled;
    $[107] = parallax.car;
    $[108] = parallax.scene;
    $[109] = resolvedCarReady;
    $[110] = t37;
    $[111] = t38;
    $[112] = t39;
    $[113] = t40;
    $[114] = t41;
  } else {
    t41 = $[114];
  }
  let t42;
  if ($[115] !== parallax.scene || $[116] !== specialLogo || $[117] !== title) {
    t42 = <SpartanProductHeroBannerSpecialLogoScene title={title} specialLogo={specialLogo} scene={parallax.scene} />;
    $[115] = parallax.scene;
    $[116] = specialLogo;
    $[117] = title;
    $[118] = t42;
  } else {
    t42 = $[118];
  }
  let t43;
  if ($[119] !== t35 || $[120] !== t36 || $[121] !== t41 || $[122] !== t42) {
    t43 = <div className={t35}>{t36}{t41}{t42}</div>;
    $[119] = t35;
    $[120] = t36;
    $[121] = t41;
    $[122] = t42;
    $[123] = t43;
  } else {
    t43 = $[123];
  }
  let t44;
  if ($[124] !== sceneLayoutStyle || $[125] !== t43) {
    t44 = <div className={t32} style={sceneLayoutStyle}>{t43}</div>;
    $[124] = sceneLayoutStyle;
    $[125] = t43;
    $[126] = t44;
  } else {
    t44 = $[126];
  }
  let t45;
  if ($[127] !== t29 || $[128] !== t30 || $[129] !== t31 || $[130] !== t44 || $[131] !== t8 || $[132] !== t9) {
    t45 = <div className={styles.scrollScene} style={t8} {...t9} {...t10}>{t29}{t30}{t31}{t44}</div>;
    $[127] = t29;
    $[128] = t30;
    $[129] = t31;
    $[130] = t44;
    $[131] = t8;
    $[132] = t9;
    $[133] = t45;
  } else {
    t45 = $[133];
  }
  return t45;
};